import { useLayoutEffect, useRef, useState } from 'react';
import useViewport from './useViewport';
import { isAppActive, subscribeAppActivity } from '../utils/appActivity';

export default function usePageLayout(headerRef, footerRef, { headerVisible = true, footerVisible = true } = {}) {
  const viewport = useViewport();
  const [sizes, setSizes] = useState({ headerHeight: 0, footerHeight: 0, safeTop: 0 });
  const observed = useRef(null);

  // Check the nodes after each render (a loading screen can attach them
  // later), but subscribe and measure only when those nodes change.
  // Typing/voting must not rebuild observers or force layout on every update.
  useLayoutEffect(() => {
    const header = headerRef?.current;
    const footer = footerRef?.current;
    if (observed.current?.header === header && observed.current?.footer === footer) return;
    observed.current?.dispose();
    const measure = () => {
      if (!isAppActive()) return;
      const next = {
        headerHeight: header?.getBoundingClientRect().height || 0,
        footerHeight: footer?.getBoundingClientRect().height || 0,
        safeTop: header ? parseFloat(getComputedStyle(header).paddingTop) || 0 : 0,
      };
      setSizes((previous) => Object.keys(next).every((key) => next[key] === previous[key]) ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    // Safe-area padding can change without a content-box resize. Observe the
    // same outer box we measure, so fixed content never slips under controls.
    if (header) observer?.observe(header, { box: 'border-box' });
    if (footer) observer?.observe(footer, { box: 'border-box' });
    window.addEventListener('resize', measure);
    const unsubscribe = subscribeAppActivity(event => {
      if (event.active && event.reason === 'resume') measure();
    });
    const dispose = () => {
      unsubscribe();
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
    observed.current = { header, footer, dispose };
  });
  useLayoutEffect(() => () => { observed.current?.dispose(); observed.current = null; }, []);

  const topSpace = headerVisible ? sizes.headerHeight : sizes.safeTop;
  return {
    ...sizes,
    contentHeight: Math.max(0, viewport.height - topSpace - (footerVisible ? sizes.footerHeight : 0)),
    contentTop: viewport.offsetTop + topSpace,
    viewportHeight: viewport.height,
    viewportTop: viewport.offsetTop,
    keyboardOpen: viewport.keyboardOpen,
  };
}
